import React from 'react';
import { NodeProps, useReactFlow } from 'reactflow';
import { Select } from '../../../../form/select';
import { Textarea } from '../../../../form/textarea';
import { operationType, selectDataType } from '../../../../../services/types';
import { Main, Styled } from './style';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { ExpandedBlock } from '../../../expanded';
import { TextInput } from '../../../../form/textInput';

type GeminiProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

export function WebSearch({ id, data, setChildMinimize, setChildSettings }: GeminiProps) {
   const [searchTypes, setSearchType] = React.useState<selectDataType[]>([
      { id: 1, name: 'web', isActive: true },
      { id: 2, name: 'news', isActive: false },
      { id: 3, name: 'images', isActive: false },
      { id: 4, name: 'videos', isActive: false },
   ]);
   const { setNodes } = useReactFlow();
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);

   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   React.useEffect(() => {
      if (!data.value) {
         setNodes((nds) => nds.map((node) => {
            if (node.id === id) return { ...node, data: { ...node.data, value: { searchType: searchTypes[0].name, search_query: '', max_results: 5 } } };
            else return node;
         }));
      } else {
         setSearchType(searchTypes => searchTypes.map(searchType => ({ ...searchType, isActive: searchType.name === data.value.searchType })));
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   const handleSelectSearchType = (selectId: number) => {
      setSearchType((searchTypes) => searchTypes.map((searchType) => {
         if (searchType.id === selectId) return { ...searchType, isActive: true };
         else return { ...searchType, isActive: false };
      }));
      setNodes((nds) => nds.map((node) => {
         if (node.id === id) return { ...node, data: { ...node.data, value: { ...node.data.value, searchType: searchTypes.find(m => m.id === selectId)?.name } } };
         else return node;
      }));
   }
   // ---
   const handleChangeSearchQuery = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNodes((nds) => nds.map((node) => {
         if (node.id === id) return { ...node, data: { ...node.data, value: { ...node.data.value, search_query: e.target.value } } };
         else return node;
      }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   const handleChangeMaxResults = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setNodes((nds) => nds.map((node) => {
         if (node.id === id) return { ...node, data: { ...node.data, value: { ...node.data.value, max_results: e.target.value } } };
         else return node;
      }));
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   // ---
   const isWarning = false;

   return (<>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
         {isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}

         {isExpanded ? <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ minWidth: '380px' }}
         ><Styled>
               <Select title="Search" data={searchTypes} handleSelectItem={handleSelectSearchType} isSmall />
               <Textarea title="Search Query" value={data.value?.search_query} handleChange={handleChangeSearchQuery} />
               <TextInput
                  type="number"
                  value={data.value?.max_results?.toString()}
                  title="Max Results"
                  isNormal={false}
                  placeholder="e.g. 10"
                  handleChange={handleChangeMaxResults}
                  max={200} min={1}
               />
            </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}